import { parseMultilineContent } from "@repo/app-types/content/plaintext";
import type { Company } from "@repo/app-types/entities/company";
import type {
  ProjectUpdate,
  ProjectWithInterviewCount,
} from "@repo/app-types/entities/project";
import {
  getInterviewLinkForProject,
  getProjectAndJobTypeProperty,
  JobTypeProperty,
} from "@repo/app-types/entities/project";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { InfoIconTooltip } from "@repo/ui/tooltip/infoIconTooltip";
import { useUpdateJobTypeMutation } from "../../../../api/entities/jobType.js";
import { useAppSelector } from "../../../../hooks/hook.js";
import { AppearanceSettingsSection } from "../appearanceSettingsSection.js";
import { AutoAdvanceSetting } from "../autoAdvanceSetting.js";
import { DataExtractionJobTypeSetting } from "../dataExtractionJobTypeSetting.js";
import { InterviewLanguageSetting } from "../interviewLanguageSetting.js";
import { InterviewLinkCopybox } from "../interviewLinkCopybox.js";
import { InterviewSimulationButton } from "../interviewSimulation/interviewSimulationButton.js";
import { MessagingAutomationSection } from "../messagingAutomationSection.js";
import { PhoneNumberDisplaySection } from "../phoneNumberDisplaySection.js";
import { ProjectNotificationsSettingsSection } from "../projectNotificationsSettingsSection.js";
import { JobOpeningSettings } from "./jobOpeningSettings.js";
import { JobTypeContext } from "./jobTypeContext.js";
import { JobTypeName } from "./jobTypeName.js";
import { RecruitingOtherSettings } from "./recruitingOtherSettings.js";
import { RecruitingSettingsQuestionsSection } from "./recruitingSettingsQuestionsSection.js";

interface RecruitingProjectSettingsProps {
  project: ProjectWithInterviewCount;
  company: Company;
  onSaveProject: (project: ProjectUpdate) => Promise<void>;
  onClickSharingTemplates: () => void;
}

export const RecruitingProjectSettings = ({
  project,
  company,
  onSaveProject,
  onClickSharingTemplates,
}: RecruitingProjectSettingsProps) => {
  const teammate = useAppSelector((state) => state.teammate.teammate);
  const [updateJobType] = useUpdateJobTypeMutation();

  const language = getProjectAndJobTypeProperty(
    project,
    JobTypeProperty.Language,
  );
  const multilangLanguages = getProjectAndJobTypeProperty(
    project,
    JobTypeProperty.MultilangLanguages,
  );

  const voice = getProjectAndJobTypeProperty(
    project,
    JobTypeProperty.LanguageVoice,
  );

  return (
    <div className="flex flex-col space-y-6">
      <div>
        <div className="rounded-lg border border-border-light bg-white px-4 py-3">
          <div className="mb-2 flex items-end space-x-3">
            <div className="shrink-0">
              <InterviewLinkCopybox
                linkLabel="Interview link"
                inviteLinkUrl={getInterviewLinkForProject(
                  import.meta.env.VITE_INTERVIEW_APP_BASE_URL,
                  company,
                  project,
                )}
                project={project}
                onCopyInterviewLink={() => {
                  showToast({
                    title: `Interview link copied to clipboard`,
                    variant: ToastVariant.Success,
                  });
                }}
              />
            </div>
            <div className="shrink-0">
              <Label size={Size.SMALL}>Phone number</Label>
              <PhoneNumberDisplaySection
                projectId={project.id}
                company={company}
              />
            </div>
            <div className="shrink-0">
              <InterviewSimulationButton project={project} />
            </div>
          </div>
          <div className="mb-2 text-sm text-foreground">
            Send the interview link or phone number to candidates or add it to
            your job posting (
            <span
              onClick={onClickSharingTemplates}
              className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
            >
              See Templates
            </span>
            ). Links work on desktop or mobile browsers and can be customized
            with a custom logo and messaging.
          </div>
        </div>
      </div>

      <div>
        {project.job_type ? (
          <>
            <div className="mb-2 flex flex-row items-center space-x-1">
              <Label size={Size.SMALL} variant="secondary">
                Job Type
              </Label>
              <InfoIconTooltip>
                Job Types define interview questions and job qualifications and
                can be reused across multiple job openings. For example, you may
                have a &ldquo;Store Manager&rdquo; Job Type and a Job Opening
                for each store location.
              </InfoIconTooltip>
            </div>
            <div>
              <JobTypeName jobType={project.job_type} projectId={project.id} />
            </div>
          </>
        ) : (
          <Label size={Size.LARGE}>Interview settings</Label>
        )}
        <div className="flex flex-col space-y-3">
          <div>
            <RecruitingSettingsQuestionsSection
              language={language}
              project={project}
              onSaveProject={(p) => {
                void onSaveProject(p);

                // Double write to the job type
                if (project.job_type) {
                  void updateJobType({
                    id: project.job_type.id,
                    questions: p.questions,
                    job_description: p.job_description
                      ? parseMultilineContent(p.job_description, {
                          parsesVariables: false,
                        })
                      : undefined,
                  });
                }
              }}
            />
          </div>
          {project.job_type ? (
            <DataExtractionJobTypeSetting jobTypeId={project.job_type.id} />
          ) : null}
          <JobTypeContext
            context={getProjectAndJobTypeProperty(
              project,
              JobTypeProperty.PromptContext,
            )}
            hasJobType={Boolean(project.job_type)}
            onSave={(newContext) => {
              void onSaveProject({
                id: project.id,
                settings: {
                  ...project.settings,
                  prompt_context: newContext ?? null,
                },
              });

              // Double write to the job type
              if (project.job_type) {
                void updateJobType({
                  id: project.job_type.id,
                  prompt_context: newContext
                    ? parseMultilineContent(newContext, {
                        parsesVariables: false,
                      })
                    : null,
                });
              }
            }}
          />
          <InterviewLanguageSetting
            language={language}
            multilangLanguages={multilangLanguages}
            voice={voice}
            isTrainingProject={false}
            onSave={(newLanguage, newMultilangLanguages, newVoice) => {
              void onSaveProject({
                id: project.id,
                settings: {
                  ...project.settings,
                  interview_language: newLanguage,
                  multilang_languages: newMultilangLanguages,
                  language_voice: newVoice,
                },
              });

              // Double write to the job type
              if (project.job_type) {
                void updateJobType({
                  id: project.job_type.id,
                  settings: {
                    ...project.job_type.settings,
                    interview_languages: [newLanguage],
                    multilang_languages: newMultilangLanguages,
                    language_voice: newVoice,
                  },
                });
              }
            }}
          />

          <RecruitingOtherSettings
            onSaveProject={(p) => void onSaveProject(p)}
            project={project}
          />
          <AppearanceSettingsSection
            onSaveProject={(p) => void onSaveProject(p)}
            company={company}
            project={project}
          />
          <AutoAdvanceSetting
            autoAdvanceAssessmentScoreThreshold={getProjectAndJobTypeProperty(
              project,
              JobTypeProperty.AutoAdvanceAssessmentScoreThreshold,
            )}
            onSave={(newThreshold) => {
              void onSaveProject({
                id: project.id,
                settings: {
                  ...project.settings,
                  auto_advance_assessment_score_threshold: newThreshold,
                },
              });

              // Double write to the job type
              if (project.job_type) {
                void updateJobType({
                  id: project.job_type.id,
                  settings: {
                    ...project.job_type.settings,
                    auto_advance_assessment_score_threshold: newThreshold,
                  },
                });
              }
            }}
          />
          {project.job_type ? (
            <div className="pt-4">
              <Label size={Size.SMALL} variant="secondary">
                Job Opening
              </Label>
            </div>
          ) : null}

          <div className="flex flex-col space-y-4">
            {project.job_type ? (
              <JobOpeningSettings
                project={project}
                onSaveProject={onSaveProject}
              />
            ) : null}
            <MessagingAutomationSection project={project} />
            <ProjectNotificationsSettingsSection
              project={project}
              onSaveProject={(p) => void onSaveProject(p)}
              isTrainingProject={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
